import { Link } from "react-router-dom";
import { ArrowRight, Search, Sparkles, Share2, Heart, CheckCircle2, Feather, Zap, Crown, Flower2, Gem, Flame, Moon, Briefcase, Backpack, Sun, Gift, Music } from "lucide-react";

const IMG = {
  beauty: "https://images.unsplash.com/photo-1689147782410-8630824fffa6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  fashion: "https://images.unsplash.com/photo-1710972197951-3aade7376076?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  jewellery: "https://images.unsplash.com/photo-1716504627981-22728cb2d2e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  lifestyle: "https://images.unsplash.com/photo-1625093742435-6fa192b6fb10?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

export function DiscoverHerWorld() {
  const cards = [
    ["Beauty", "Makeup, skincare & everyday glow", IMG.beauty, "/category/cosmetics", "lg:row-span-2"],
    ["Jewellery", "Earrings, necklaces & more", IMG.jewellery, "/category/jewellery", ""],
    ["Fashion", "Kurtis, tops & ethnic edits", IMG.fashion, "/category/ladies-fashion", ""],
    ["Lifestyle", "Finds that make the day special", IMG.lifestyle, "/products", "sm:col-span-2 lg:col-span-1"],
  ];
  return (
    <section className="py-14 sm:py-20" data-testid="discover-her-world">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Discover Her World</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#231518] mt-2">A Little of Everything She Loves.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-10 auto-rows-[220px] lg:auto-rows-[260px]">
          {cards.map(([name, desc, img, to, span]) => (
            <Link key={name} to={to} data-testid={`dhw-${name.toLowerCase()}`}
              className={`group relative overflow-hidden rounded-2xl shadow-soft ${span}`}>
              <img src={img} alt={name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#231518]/85 via-[#231518]/15 to-transparent" />
              <div className="absolute bottom-0 p-5 w-full text-white">
                <h3 className="font-display text-2xl font-semibold">{name}</h3>
                <p className="text-sm text-white/85 mt-0.5 line-clamp-1">{desc}</p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold mt-2 border-b border-[#D4AF37] pb-0.5">EXPLORE <ArrowRight className="w-3 h-3" /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RupeeJourney() {
  const steps = [
    ["Discover", "We hunt for pieces women will genuinely love.", Search],
    ["Curate", "Only the beautiful, useful and well-made make the cut.", Sparkles],
    ["Test", "Real women try them before they ever go live.", CheckCircle2],
    ["Share", "The community tells us what truly deserves a spot.", Share2],
    ["Love", "Beautiful things — honest prices, starting at ₹49.", Heart],
  ];
  return (
    <section className="py-14 sm:py-20 bg-[#FDF5E6] border-y border-[#EADBD8]" data-testid="rupee-journey">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#6A1B29]">The ₹49 Journey</p>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold text-[#231518] mt-3">Can Something Beautiful Really Begin at ₹49?</h2>
          <p className="text-[#755C62] mt-3">Yes — when it's discovered with care, tested by real women and loved by the community.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
          {steps.map(([title, desc, Icon], i) => (
            <div key={title} className="bg-white border border-[#EADBD8] rounded-2xl p-5 shadow-soft" data-testid={`journey-step-${i}`}>
              <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#6A1B29] text-white"><Icon className="w-5 h-5" /></span>
              <p className="text-[11px] font-semibold text-[#B08A2E] mt-3">STEP {i + 1}</p>
              <h3 className="font-display text-xl font-semibold text-[#231518] mt-0.5">{title}</h3>
              <p className="text-xs text-[#755C62] mt-1.5 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/products" data-testid="journey-cta" className="inline-flex items-center gap-2 bg-[#6A1B29] hover:bg-[#501325] text-white font-bold px-8 py-3.5 rounded-full transition-transform hover:scale-105">Explore ₹49 Finds <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
}

export function HonestPrices() {
  const tiers = [
    ["₹49", "Finds", "from-[#F9ECE8] to-[#FFF0ED]"],
    ["Under ₹99", "Little Treats", "from-[#FDF5E6] to-[#FBEFD8]"],
    ["Under ₹199", "Everyday Loves", "from-[#F3E3E6] to-[#F9ECE8]"],
    ["Under ₹499", "Something Special", "from-[#EFE3EC] to-[#F6ECE9]"],
  ];
  return (
    <section className="py-14 sm:py-20 bg-[#FFF0ED]" data-testid="honest-prices">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Beautiful Things. Honest Prices.</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#231518] mt-2">Affordable Never Looked So Lovely.</h2>
          <p className="text-[#755C62] mt-2">Discover products that feel special and stay easy on your pocket. Affordable ≠ cheap.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
          {tiers.map(([price, label, grad]) => (
            <Link key={price} to="/products" data-testid={`price-tier-${price.replace(/[^0-9]/g, "") || "49"}`}
              className={`group rounded-2xl bg-gradient-to-br ${grad} border border-[#EADBD8] p-6 shadow-soft hover:shadow-lift transition-all hover:-translate-y-0.5`}>
              <p className="font-display text-3xl sm:text-4xl font-bold text-[#6A1B29]">{price}</p>
              <p className="text-sm font-semibold text-[#231518] mt-1">{label}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#6A1B29] mt-4 group-hover:gap-2 transition-all">Shop now <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MadeForHer() {
  const personas = [
    ["The Minimalist", "Clean, classic, effortless", Feather],
    ["The Trendsetter", "Always a step ahead", Zap],
    ["The Glam Girl", "Bold lips, big energy", Sparkles],
    ["The Classic Girl", "Timeless & elegant", Crown],
    ["The Self-Care Lover", "Soft days, good skin", Flower2],
    ["The Jewellery Lover", "Gold on gold, always", Gem],
  ];
  return (
    <section className="py-14 sm:py-20" data-testid="made-for-her">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Made For Her</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#231518] mt-2">Whoever She Is Today.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10">
          {personas.map(([name, desc, Icon]) => (
            <Link key={name} to="/discover" data-testid={`persona-${name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group flex items-center gap-4 bg-white border border-[#EADBD8] rounded-2xl p-5 shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all">
              <span className="w-12 h-12 grid place-items-center rounded-xl bg-[#F9ECE8] shrink-0"><Icon className="w-5 h-5 text-[#6A1B29]" /></span>
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-[#231518]">{name}</h3>
                <p className="text-sm text-[#755C62] line-clamp-1">{desc}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#6A1B29] ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ShopByOccasion() {
  const occ = [
    ["Diwali Looks", Flame], ["Wedding Season", Gem], ["Festive Beauty", Sparkles], ["Date Night", Moon],
    ["Office Style", Briefcase], ["College Style", Backpack], ["Everyday Essentials", Sun], ["Gifting", Gift],
    ["Self-Care Sunday", Heart], ["Girls' Night Out", Music],
  ];
  return (
    <section className="py-14 sm:py-20 bg-[#FFF0ED]" data-testid="shop-by-occasion">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Shop By Occasion</p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#231518] mt-2">For Every Moment That Matters.</h2>
        <div className="flex flex-wrap gap-3 mt-8">
          {occ.map(([label, Icon]) => (
            <Link key={label} to="/products" data-testid={`occasion-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="inline-flex items-center gap-2 bg-white border border-[#EADBD8] rounded-full pl-3.5 pr-5 py-2.5 shadow-soft hover:border-[#6A1B29] hover:text-[#6A1B29] transition-colors text-sm font-semibold text-[#231518]">
              <Icon className="w-4 h-4 text-[#6A1B29]" /> {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
